import React, { isValidElement, type ReactNode } from "react";
import { SKOLTEND_INTRO_FAQS } from "./skoltendIntroFaqs";

// Shared by the interactive page and build-time HTML. Public editorial content only.
export const FAQ_SECTIONS = [
  {
    section: "關於 Skoltend",
    items: SKOLTEND_INTRO_FAQS.map(({ question, answer }) => ({
      q: question,
      keywords: `${question} ${answer}`,
      a: <p>{answer}</p>,
    })),
  },
  {
    section: "關於學校招標與採購流程",
    items: [
      {
        q: "香港有哪個平台可以集中尋找學校招標及書面報價公告？",
        keywords: "香港 平台 集中 學校 招標 書面報價 公告 skoltend 採購 中學 小學 特殊學校 供應商 教育",
        a: (
          <>
            <p>
              <strong>skoltend.com</strong> 是專為香港中學、小學及特殊學校而設的招標及書面報價配對平台。平台集中發佈全港各區學校的最新採購需求、招標公告及書面報價邀請，協助供應商更有效率地尋找教育界的商業機會。
            </p>
          </>
        ),
      },
      {
        q: "Skoltend 與其他綜合採購平台有何本質上的不同？",
        keywords: "Skoltend 採購平台 分別 比較 本質 專心 專業 專注 香港學校 招標 書面報價 資訊服務 公平競爭 中小企 1100 大數據 自動化",
        a: (
          <>
            <p>
              Skoltend 只做一件事，而且要做到全港<strong>最快、最新、最齊</strong>——香港中小學及特殊學校的招標與書面報價資訊匯集。我們以「<strong>專心、專業、專注</strong>」為核心理念，每日透過大數據與自動化技術整合全港 <strong>1,100+ 間</strong>學校的動態。
            </p>
            <p className="mt-2">
              我們深知在商場上「第一名永遠只有一個」，但 Skoltend 致力於為中小企打造一個<strong>公平競技的舞台</strong>：只要供應商願意落場參賽、不斷根據學校需求改善服務與修正標書質量，透過持續的努力與精準的資訊掌握，絕對能夠後來居上，逐步追趕並超越市場領先者。
            </p>
          </>
        ),
      },
      {
        q: "為什麼 Skoltend 要堅定不移地專注於「學校招標」這條賽道？",
        keywords: "專注 賽道 學校招標 香港經濟 轉型 結業潮 中小企 逆市 穩健增長 半政府 教育局 撥款 年度預算 現金流 安全 走數 資金鏈",
        a: (
          <>
            <p>
              在當前香港經濟環境面對轉型與挑戰、各行各業頻現結業潮的背景下，傳統商業市場的競爭往往陷入價格戰與高風險的泥沼。Skoltend 深信，學校招標與書面報價市場是中小企業在逆市中尋求穩健增長的<strong>「黃金新賽道」</strong>。
            </p>
            <p className="mt-2">
              學校屬於半政府資助機構，享有教育局穩定的定期項目撥款，且每年均有必須有效善用的年度財政預算。這意味著學校採購<strong>完全不受一般經濟週期波動影響</strong>，更絕對不存在「走數」或資金鏈斷裂的風險，為供應商提供了一個極度安全、現金流有保障的優質商業生態。
            </p>
          </>
        ),
      },
      {
        q: "學校進行採購時，什麼情況下需要進行公開招標，什麼時候只需書面報價？",
        keywords: "公開招標 書面報價 採購 金額 界線 5000 50000 200000 兩間 五間 供應商 學校 規定 指引",
        a: (
          <>
            <p>根據一般學校採購指引，採購金額分為以下情況：</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-muted-foreground pl-2">
              <li>
                <strong>$5,001 – $50,000：</strong>需邀請最少 <strong>兩間</strong> 供應商提供書面報價
              </li>
              <li>
                <strong>$50,001 – $200,000：</strong>需邀請最少 <strong>五間</strong> 供應商提供書面報價
              </li>
              <li>
                <strong>$200,000 以上：</strong>必須進行嚴格的 <strong>公開招標</strong> 程序
              </li>
            </ul>
          </>
        ),
      },
      {
        q: "Skoltend 如何協助供應商打破教育採購市場的資訊不對稱？",
        keywords: "資訊不對稱 大數據 爬蟲 自動化 1100 學校 整合 18區 行業分類 中小企 供應商 公平 公正 商機 招標 書面報價 佈告板",
        a: (
          <>
            <p>
              傳統教育採購市場長期存在資訊壁壘，學校的書面報價與招標公告往往分散於各校網頁、不同佈告板或僅透過舊有承辦商名冊傳遞。Skoltend 運用大數據與自動化爬蟲技術，每日系統化整合全港 <strong>1,100+ 間</strong>中小學及特殊學校的最新招標與採購需求。
            </p>
            <p className="mt-2">
              我們將分散的數據集中歸納，並按 <strong>18 區</strong>及行業分類精準呈現，讓中小企業與大型供應商站在同一起跑線上，公平、公正、高效地獲取商機。
            </p>
          </>
        ),
      },
      {
        q: "Skoltend 收集的學校招標數據來源是否可靠？",
        keywords: "數據來源 可靠 準確 官方 渠道 校驗 發佈日期 截止日期 行業分類 項目名稱 時效性 真實 公告 教育團體",
        a: (
          <>
            <p>
              本平台的招標與書面報價資訊全部直接源自香港各中小學、教育團體及公開官方渠道的真實公告。我們的團隊與系統經過嚴格校驗，確保每一項招標的<strong>發佈日期、截止日期、行業分類及項目名稱</strong>均具備高度準確性與時效性，免除供應商逐一瀏覽數百個學校網頁的繁瑣耗時。
            </p>
          </>
        ),
      },
    ],
  },
  {
    section: "供應商／承辦商常見疑問",
    items: [
      {
        q: "承辦商如何登記收取最新中學及小學招標資訊？",
        keywords: "承辦商 登記 中學 小學 招標 資訊 通知 Email Telegram 會員 付費 業務分類 文具 資訊科技 IT 工程維修 免費註冊",
        a: (
          <>
            <p>只需三個步驟：</p>
            <ol className="mt-2 space-y-1 list-decimal list-inside text-muted-foreground pl-2">
              <li>在 skoltend.com 免費註冊帳戶</li>
              <li>設定業務分類（如：文具供應、資訊科技、工程維修）</li>
              <li>升級為付費會員，即可接收 Email 及 Telegram 招標通知</li>
            </ol>
            <p className="mt-2">當有學校發佈相關類別的招標或書面報價邀請時，系統會為您即時提示最新資訊。</p>
          </>
        ),
      },
      {
        q: "免費會員與付費會員在功能上有什麼具體分別？",
        keywords: "免費會員 付費會員 功能 分別 比較 學校名稱 聯絡方式 招標文件 Email Telegram 通知 升級 會籍 方案",
        a: (
          <>
            <p>為了切合不同規模供應商的需求，Skoltend 提供靈活的會籍方案：</p>
            <ul className="mt-2 space-y-2 text-muted-foreground pl-2">
              <li>
                <strong className="text-foreground">免費會員：</strong>可瀏覽全港最新招標項目的基本概覽、行業分佈及地區分佈，掌握市場宏觀走勢。
              </li>
              <li>
                <strong className="text-foreground">付費會員：</strong>解鎖核心獲客功能，包括查看學校具體名稱、直接聯絡方式、詳細招標文件連結，並享有每日 <strong>Email 及 Telegram 即時招標通知</strong>，確保在投標期限前搶佔先機。
              </li>
            </ul>
          </>
        ),
      },
      {
        q: "為什麼我提交了報價或標書後，往往沒有收到學校的直接回覆？",
        keywords: "報價 標書 提交 回覆 學校 等待 評審委員會 校董會 採購委員會 中標 跟進 查詢 截標 技術評審 價格對比 審議",
        a: (
          <>
            <p>
              香港學校在處理書面報價及公開招標時，必須嚴格遵守教育局的採購條例與採購委員會審批程序。一般而言，校方需要經過<strong>技術評審、價格對比、校董會或採購小組審議</strong>等多重關卡。
            </p>
            <p className="mt-2">
              基於行政慣例與公平原則，<strong>學校通常只會主動聯絡最終中標或成功獲選的承辦商</strong>。若在截標後數週未獲通知，建議透過平台的過往紀錄進行覆盤或禮貌查詢。
            </p>
          </>
        ),
      },
      {
        q: "投標學校項目，需要具備什麼資格或證明？",
        keywords: "投標 資格 證明 商業登記 BR 牌照 小型工程 食環署 服務紀錄 Job Reference 防貪 誠信 條款 文件",
        a: (
          <>
            <p>雖然各學校要求不同，但一般會要求承辦商提供以下文件：</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-muted-foreground pl-2">
              <li>有效的商業登記證（BR）</li>
              <li>相關行業的牌照或註冊證明（如小型工程牌照、食環署牌照）</li>
              <li>過往為學校或政府機構提供服務的參考紀錄（Job Reference）</li>
              <li>無衝突申報及防貪誠信條款同意書</li>
            </ul>
          </>
        ),
      },
      {
        q: "投標學校項目，如何確保評審過程公平公正？",
        keywords: "評審 公平 公正 透明 廉政 防貪 法團校董會 教育局 操守 道德 不透明 利益 以權謀私 中小企 競標",
        a: (
          <>
            <p>
              部分供應商常擔心投標是否涉及不透明操作。Skoltend 對此充滿信心：香港的教育工作者與教師群體普遍具備<strong>極高的專業操守與道德標準</strong>，教育局及各校法團校董會亦有極其嚴格的廉政與防貪監管機制，學校採購絕不可能容忍收受利益或以權謀私。
            </p>
            <p className="mt-2">
              因此，只要您的產品或服務<strong>性價比高、質素優良、完全切合學校教學與行政所需</strong>，每一家中小企都能夠憑藉實力公平競標、脫穎而出。
            </p>
          </>
        ),
      },
      {
        q: "如果忘記登入密碼或帳戶出現異常，應該如何處理？",
        keywords: "忘記密碼 登入問題 帳戶異常 重設密碼 電郵 客戶服務 一個工作天 聯絡我們 帳戶存取",
        a: (
          <>
            <p>
              若您遇到登入問題，可隨時點擊登入頁面的<strong>「忘記密碼」</strong>連結，系統將發送重設密碼指示至您的註冊電郵。
            </p>
            <p className="mt-2">
              若問題持續，您可透過網站底部的「聯絡我們」專頁與我們的客戶服務團隊聯繫，我們將於<strong>一個工作天內</strong>協助您恢復帳戶存取權。
            </p>
          </>
        ),
      },
      {
        q: "IT 招標有哪些類別？平台涵蓋哪些資訊科技採購？",
        keywords: "IT 招標 資訊科技 採購 電腦 網絡 伺服器 軟件 硬件 維修 支援 系統 資訊保安 CCTV 閉路電視 電話系統",
        a: (
          <>
            <p>平台涵蓋的 IT 及資訊科技相關採購類別包括：</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-muted-foreground pl-2">
              <li>電腦設備（桌面電腦、手提電腦、平板電腦）採購</li>
              <li>校園網絡及 Wi-Fi 基礎設施</li>
              <li>伺服器、儲存設備及雲端服務</li>
              <li>教學軟件及授權</li>
              <li>IT 技術支援及維護合約</li>
              <li>資訊保安解決方案</li>
              <li>CCTV 閉路電視及門禁系統</li>
              <li>電話及通訊系統</li>
            </ul>
          </>
        ),
      },
    ],
  },
];

export const ALL_ITEMS = FAQ_SECTIONS.flatMap(({ section, items }) =>
  items.map(item => ({ ...item, section }))
);

function answerText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(answerText).join(" ");
  if (isValidElement<{ children?: ReactNode }>(node)) return answerText(node.props.children);
  return "";
}

export function faqSchema(items: { q: string; a: ReactNode }[] = ALL_ITEMS) {
  return {
    "@type": "FAQPage",
    "@id": "https://www.skoltend.com/faq#faq",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question", name: q,
      acceptedAnswer: { "@type": "Answer", text: answerText(a).replace(/\s+/g, " ").trim() },
    })),
  };
}
